/**
 * ListFacturasCxP.js — Lista de Facturas CxP (FacturaCxP)
 * Mismo estilo exacto que ListInvoices de CxC:
 * - invoice.module.css clases
 * - ActionBtn idéntico al de InvoiceRow
 * - Columna Gasto/Período igual que Rubro/Período de CxC
 * - Fecha DD/MM/YYYY
 * - Monto text-end monospace
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { ConfirmModal } from "../../../../shared/components/SharedUI";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import ImportExportBar from "../../shared/ImportExportBar";

/** "2026-04-15" → "15/04/2026" */
function fmtDate(iso) {
  if (!iso || iso.length < 10) return iso ?? "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

const MESES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

function getMes(concepto, fecha) {
  const c = (concepto || "").toLowerCase();
  const idx = MESES.findIndex(m => c.includes(m.toLowerCase()));
  if (idx !== -1) return MESES[idx];
  if (fecha?.length >= 7) return MESES[parseInt(fecha.split("-")[1], 10) - 1] ?? "";
  return "";
}

// ── ActionButtons — estilo Residentes (pequeño, colorido) ────────────────────
function ActionButtons({ onVer, onEdit, onDelete }) {
  const btn = (onClick, bg, border, color, icon, title) => (
    <button type="button" onClick={onClick} title={title} style={{
      background: bg, border: `1px solid ${border}`,
      borderRadius: 6, padding: "4px 10px",
      color, cursor: "pointer",
    }}>
      <i className={`fa-solid ${icon}`} />
    </button>
  );
  return (
    <div className="d-flex align-items-center justify-content-center" style={{ gap: 6 }}>
      {onVer   && btn(onVer,    "rgba(96,165,250,0.1)",  "rgba(96,165,250,0.3)",  "#60a5fa", "fa-eye",        "Ver detalle")}
      {onEdit  && btn(onEdit,   "rgba(249,115,22,0.1)",  "rgba(249,115,22,0.3)",  "#f97316", "fa-square-pen", "Editar")}
      {onDelete && btn(onDelete, "rgba(239,68,68,0.1)",  "rgba(239,68,68,0.25)", "#f87171", "fa-trash",      "Eliminar")}
    </div>
  );
}

// ── Fila — igual que InvoiceRow de CxC ───────────────────────────────────────
function FacturaCxPRow({ fac, onVer, onEdit, onDelete }) {
  const nombreGasto = fac.cuenta_gasto?.nombre ?? fac.concepto ?? "—";
  const mes = getMes(fac.concepto, fac.fecha_emision);

  return (
    <tr>
      {/* N° Doc Proveedor — número que el proveedor asignó */}
      <td style={{ fontWeight: 600, color: "#f9fafb", fontFamily: "monospace" }}>
        {fac.numero_doc_proveedor || "—"}
        <span style={{ display: "block", fontSize: "0.72rem",
          color: "#6b7280", fontWeight: 400, marginTop: 1 }}>
          {fac.numero_factura}
        </span>
      </td>

      {/* Gasto / Período — nombre arriba, mes abajo en gris pequeño */}
      <td>
        <span style={{ color: "#f9fafb", fontWeight: 500 }}>{nombreGasto}</span>
        {mes && (
          <span style={{ display: "block", fontSize: "0.75rem",
            color: "#9ca3af", marginTop: 1 }}>
            {mes}
          </span>
        )}
      </td>

      {/* Proveedor */}
      <td style={{ fontSize: 15 }}>
        {fac.cuenta_proveedor?.nombre ?? "—"}
      </td>

      {/* Fecha DD/MM/YYYY */}
      <td>{fmtDate(fac.fecha_emision)}</td>

      {/* Monto — text-end monospace para alineación uniforme */}
      <td>
        <div className="monto-typo" style={{ fontWeight: 600, textAlign:"right" }}>
          ₡{formatCurrency(fac.monto)}
        </div>
      </td>

      {/* Acciones */}
      <td className="text-center">
        <ActionButtons onVer={onVer} onEdit={onEdit} onDelete={onDelete} />
      </td>
    </tr>
  );
}

// ── Modal editar factura CxP ─────────────────────────────────────────────────
const OVERLAY_F = { position:"fixed", inset:0, background:"rgba(0,0,0,0.65)",
  backdropFilter:"blur(3px)", zIndex:1000 };
const MODAL_F = { position:"fixed", top:"50%", left:"50%", transform:"translate(-50%,-50%)",
  background:"#1a2535", border:"1px solid #2d3b4e", borderRadius:12, padding:24,
  width:"min(540px,94vw)", zIndex:1001, boxShadow:"0 24px 64px rgba(0,0,0,0.55)",
  maxHeight:"90vh", overflowY:"auto" };
const LABEL_F = { display:"block", fontSize:"0.78rem", fontWeight:600,
  color:"#9ca3af", marginBottom:5 };
const INP_F = { width:"100%", background:"#111827", border:"1px solid #374151",
  borderRadius:8, padding:"9px 12px", color:"#f9fafb", fontSize:"0.9rem",
  outline:"none", boxSizing:"border-box", transition:"border-color 0.15s", colorScheme:"dark" };

function EditFacturaModal({ fac, onClose, onSaved }) {
  const [cuentasGasto,     setCuentasGasto]     = useState([]);
  const [cuentasPasivo,    setCuentasPasivo]    = useState([]);
  const [form, setForm] = useState({
    numero_doc_proveedor: fac.numero_doc_proveedor ?? "",
    concepto:             fac.concepto ?? "",
    fecha_emision:        fac.fecha_emision ?? "",
    fecha_vencimiento:    fac.fecha_vencimiento ?? "",
    monto:                String(fac.monto ?? ""),
    cuenta_gasto:         fac.cuenta_gasto?.id ?? "",
    cuenta_proveedor:     fac.cuenta_proveedor?.id ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  useEffect(() => {
    apiFetch("/api/finance/catalogo/detalle/?tipo=GASTO&page_size=500")
      .then(r => r.json()).then(d => setCuentasGasto(Array.isArray(d) ? d : (d.results ?? [])));
    apiFetch("/api/finance/catalogo/detalle/?tipo=PASIVO&padre_nombre=Cuentas+por+Pagar+Locales&page_size=500")
      .then(r => r.json()).then(d => setCuentasPasivo(Array.isArray(d) ? d : (d.results ?? [])));
  }, []);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!form.numero_doc_proveedor.trim()) { setError("El N° Doc Proveedor es requerido."); return; }
    if (!form.concepto.trim())             { setError("El concepto es requerido."); return; }
    if (!form.fecha_emision)               { setError("La fecha de emisión es requerida."); return; }
    if (Number(form.monto) <= 0)           { setError("El monto debe ser mayor a 0."); return; }
    if (!form.cuenta_gasto)                { setError("Seleccioná la cuenta de gasto."); return; }
    if (!form.cuenta_proveedor)            { setError("Seleccioná la cuenta del proveedor."); return; }
    setSaving(true); setError("");
    try {
      const res = await apiFetch(`/api/finance/cxp/${fac.id}/`, {
        method: "PATCH",
        body: JSON.stringify({
          numero_doc_proveedor: form.numero_doc_proveedor.trim(),
          concepto:             form.concepto.trim(),
          fecha_emision:        form.fecha_emision,
          fecha_vencimiento:    form.fecha_vencimiento || null,
          monto:                Number(form.monto),
          // Send null (not 0) when no account selected — DRF PrimaryKeyRelatedField requires null
          cuenta_gasto:         form.cuenta_gasto ? Number(form.cuenta_gasto) : null,
          cuenta_proveedor:     form.cuenta_proveedor ? Number(form.cuenta_proveedor) : null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error('PATCH /cxp/ error:', JSON.stringify(err));
        const msgs = Object.entries(err).map(([k,v]) =>
          `${k}: ${Array.isArray(v) ? v[0] : v}`
        );
        throw new Error(msgs.join(' | ') || 'Error al actualizar la factura.');
      }
      const updated = await res.json();
      onSaved("Factura actualizada correctamente.", updated);
      onClose();
    } catch (ex) { setError(ex.message); }
    finally { setSaving(false); }
  }

  const field = (label, children, span2 = false) => (
    <div style={span2 ? { gridColumn:"span 2" } : {}}>
      <label style={LABEL_F}>{label}</label>
      {children}
    </div>
  );

  return (
    <>
      <div onClick={onClose} style={OVERLAY_F}/>
      <div style={MODAL_F}>
        <div style={{ display:"flex", justifyContent:"space-between",
          alignItems:"center", marginBottom:16, paddingBottom:12,
          borderBottom:"1px solid #2d3b4e" }}>
          <div>
            <h3 style={{ margin:0, color:"#f1f5f9", fontSize:"1rem", fontWeight:700 }}>
              <i className="fa-solid fa-square-pen" style={{ color:"#f97316", marginRight:8 }}/>
              Editar Factura CxP
            </h3>
            <p style={{ margin:"4px 0 0", color:"#64748b", fontSize:"0.8rem" }}>
              {fac.numero_factura} · El asiento contable se actualizará automáticamente
            </p>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none",
            color:"#64748b", cursor:"pointer", fontSize:"1.2rem" }}>
            <i className="fa-solid fa-xmark"/>
          </button>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px 16px" }}>
          {field("N° Doc Proveedor *",
            <input type="text" style={INP_F} value={form.numero_doc_proveedor}
              onChange={e => set("numero_doc_proveedor", e.target.value)}
              onFocus={e => e.target.style.borderColor="#f97316"}
              onBlur={e  => e.target.style.borderColor="#374151"}
              placeholder="Ej: F-12345"/>
          )}
          {field("Monto *",
            <>
              <input type="number" min="0.01" step="0.01" style={INP_F} value={form.monto}
                onChange={e => set("monto", e.target.value)}
                onFocus={e => e.target.style.borderColor="#f97316"}
                onBlur={e  => e.target.style.borderColor="#374151"}/>
              {Number(form.monto) > 0 && (
                <div style={{ fontSize:"0.73rem", color:"#f97316", marginTop:3, fontFamily:"monospace" }}>
                  ₡{Number(form.monto).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              )}
            </>
          )}
          {field("Fecha emisión *",
            <input type="date" style={INP_F} value={form.fecha_emision}
              onChange={e => set("fecha_emision", e.target.value)}
              onFocus={e => e.target.style.borderColor="#f97316"}
              onBlur={e  => e.target.style.borderColor="#374151"}/>
          )}
          {field("Fecha vencimiento",
            <input type="date" style={INP_F} value={form.fecha_vencimiento}
              onChange={e => set("fecha_vencimiento", e.target.value)}
              onFocus={e => e.target.style.borderColor="#f97316"}
              onBlur={e  => e.target.style.borderColor="#374151"}/>
          )}
          {field("Gasto — DEBE (500) *",
            <select style={{ ...INP_F, cursor:"pointer" }} value={String(form.cuenta_gasto)}
              onChange={e => set("cuenta_gasto", e.target.value)}>
              <option value="">Seleccioná cuenta de gasto…</option>
              {cuentasGasto.map(c => <option key={c.id} value={String(c.id)}>{c.codigo} — {c.nombre}</option>)}
            </select>
          )}
          {field("Proveedor — HABER (200) *",
            <select style={{ ...INP_F, cursor:"pointer" }} value={String(form.cuenta_proveedor)}
              onChange={e => set("cuenta_proveedor", e.target.value)}>
              <option value="">Seleccioná proveedor…</option>
              {cuentasPasivo.map(c => <option key={c.id} value={String(c.id)}>{c.codigo} — {c.nombre}</option>)}
            </select>
          )}
          {field("Concepto *",
            <input type="text" style={INP_F} value={form.concepto}
              onChange={e => set("concepto", e.target.value)}
              onFocus={e => e.target.style.borderColor="#f97316"}
              onBlur={e  => e.target.style.borderColor="#374151"}
              placeholder="Ej: Servicio contable — Abril 2026"/>,
            true
          )}
        </div>

        {error && (
          <div style={{ marginTop:12, padding:"10px 14px",
            background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.3)",
            borderRadius:8, color:"#f87171", fontSize:"0.84rem",
            display:"flex", alignItems:"center", gap:8 }}>
            <i className="fa-solid fa-circle-exclamation"/>{error}
          </div>
        )}

        <div style={{ display:"flex", justifyContent:"flex-end", gap:10, marginTop:16,
          paddingTop:12, borderTop:"1px solid #2d3b4e" }}>
          <button onClick={onClose} disabled={saving} style={{ padding:"8px 16px",
            background:"transparent", border:"1px solid #2d3b4e",
            borderRadius:7, color:"#64748b", fontSize:"0.84rem", cursor:"pointer" }}>
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving} style={{ padding:"8px 20px",
            background: saving ? "rgba(249,115,22,0.5)" : "#f97316",
            color:"#fff", border:"none", borderRadius:7, fontSize:"0.84rem",
            fontWeight:600, cursor: saving ? "not-allowed" : "pointer",
            display:"flex", alignItems:"center", gap:7 }}>
            {saving
              ? <><i className="fa-solid fa-circle-notch fa-spin"/> Guardando…</>
              : <><i className="fa-solid fa-floppy-disk"/> Guardar cambios</>}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function ListFacturasCxP() {
  const [items,        setItems]        = useState([]);
  const [count,        setCount]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [search,       setSearch]       = useState("");
  const [loading,      setLoading]      = useState(true);
  const [deleting,     setDeleting]     = useState(null);
  const [delLoad,      setDelLoad]      = useState(false);
  const [actionMsg,    setActionMsg]    = useState(null);
  const [detalle,      setDetalle]      = useState(null);
  const [editando,     setEditando]     = useState(null);
  const [importResult, setImportResult] = useState(null);

  const PAGE_SIZE  = 20;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const fetchData = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams({ page, page_size: PAGE_SIZE });
    if (search.trim()) qs.set("search", search.trim());
    apiFetch(`/api/finance/cxp/?${qs}`)
      .then(r => r.json())
      .then(d => { setItems(d.results ?? []); setCount(d.count ?? 0); })
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function showMsg(ok, text) {
    setActionMsg({ ok, text });
    setTimeout(() => setActionMsg(null), 4000);
  }

  const handleImportSuccess = useCallback(() => {
    setPage(1);
    fetchData();
  }, [fetchData]);

  function handleImportResult(resultado) {
    setImportResult(resultado);
  }

  async function handleDelete() {
    if (!deleting) return;
    setDelLoad(true);
    try {
      const res = await apiFetch(`/api/finance/cxp/${deleting.id}/`, { method: "DELETE" });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Error al eliminar");
      }
      setDeleting(null);
      showMsg(true, `Factura ${deleting.numero_factura} eliminada correctamente.`);
      setItems(prev => prev.filter(f => f.id !== deleting.id));
      setCount(c => c - 1);
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setDelLoad(false);
    }
  }

  return (
    <>
      {/* Breadcrumb */}
      <span className={invoiceStyles.inactive}>
        <Link to="/">
          <span className={invoiceStyles.inactive}>Inicio /</span>
        </Link>
        <Link to="/finanzas/cxp">
          <span className={invoiceStyles.inactive}> Cuentas por Pagar /</span>
        </Link>
        <span className={invoiceStyles.active}> Facturas</span>
      </span>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2"
        style={{ marginBottom: 6 }}>
        <div>
          <h2 className="mt-2 me-2 p-0 title">Gestión de Facturas</h2>
          <p className="mb-3" style={{color:"#9ca3af"}}>Administra los documentos emitidos por proveedores al condominio</p>
        </div>
        <div className="d-flex align-items-center flex-wrap gap-2">
          <ImportExportBar
            plantillaUrl="/api/finance/cxp/plantilla-excel/"
            importarUrl="/api/finance/cxp/importar-excel/"
            plantillaFilename="plantilla_facturas_cxp.xlsx"
            onImportSuccess={handleImportSuccess}
            onResult={handleImportResult}
          />
          <Link to="/finanzas/cxp/facturas/agregar" className="btn"
            style={{ padding: "9px 18px", background: "#f97316", borderRadius: 6,
              color: "#fff", textDecoration: "none", fontWeight: 600,
              fontSize: 14, display: "flex", alignItems: "center", gap: 7, border: "none" }}>
            <i className="fa-solid fa-plus" /> Nueva Factura CxP
          </Link>
        </div>
      </div>

      {/* Resultado de importación */}
      {importResult && (
        <div style={{
          width: "100%", borderRadius: 8, marginBottom: 14,
          background: importResult.ok ? "rgba(74,222,128,0.07)" : "rgba(248,113,113,0.07)",
          border: `1px solid ${importResult.ok ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)"}`,
          borderLeft: `4px solid ${importResult.ok ? "#4ade80" : "#f87171"}`,
        }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
            padding:"10px 16px",
            borderBottom: importResult.errores?.length > 0 ? "1px solid rgba(55,65,81,0.4)" : "none" }}>
            <span style={{ fontWeight:600, fontSize:"0.88rem",
              color: importResult.ok ? "#bbf7d0" : "#fecaca" }}>
              {importResult.msg}
            </span>
            <button onClick={() => setImportResult(null)}
              style={{ background:"none", border:"none", color:"#6b7280", cursor:"pointer", fontSize:"1rem" }}>✕</button>
          </div>
          {importResult.errores?.length > 0 && (
            <div style={{ padding:"10px 16px" }}>
              <details>
                <summary style={{ cursor:"pointer", color:"#fb923c", fontWeight:600, fontSize:"0.82rem" }}>
                  {importResult.errores.length} fila(s) con error — clic para ver
                </summary>
                <ul style={{ marginTop:8, paddingLeft:18, color:"#fca5a5", fontSize:"0.8rem" }}>
                  {importResult.errores.map((e, i) => (
                    <li key={i}>
                      <strong>Fila {e.fila}:</strong>{" "}
                      {e.errores?.join(" · ") ?? e}
                      {e.advertencia && <span style={{ color:"#fbbf24", marginLeft:6 }}>(advertencia)</span>}
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          )}
        </div>
      )}

      {/* Toast */}
      {actionMsg && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 2000,
          background: actionMsg.ok ? "rgba(74,222,128,0.07)" : "rgba(239,68,68,0.07)",
          border: `1px solid ${actionMsg.ok ? "rgba(74,222,128,0.3)" : "rgba(239,68,68,0.3)"}`,
          borderLeft: `4px solid ${actionMsg.ok ? "#4ade80" : "#f87171"}`,
          borderRadius: 8, padding: "12px 18px", fontSize: "0.87rem",
          display: "flex", alignItems: "center", gap: 10,
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)", maxWidth: 380,
        }}>
          <i className={`fa-solid ${actionMsg.ok ? "fa-circle-check" : "fa-circle-exclamation"}`}
             style={{ color: actionMsg.ok ? "#4ade80" : "#f87171", fontSize: "1.1rem" }} />
          <span style={{ color: actionMsg.ok ? "#4ade80" : "#f87171", fontWeight: 600 }}>
            {actionMsg.text}
          </span>
        </div>
      )}

      {/* Tabla — mismo contenedor que ListInvoices */}
      <div className={invoiceStyles["table-container"]}>

        {/* Barra búsqueda — igual que TableHeader de CxC */}
        <div className={`${invoiceStyles["table-header"]} d-flex justify-content-between`}>
          <div className={`${invoiceStyles["input-header"]} d-flex gap-2`}>
            <i className="fa-solid fa-magnifying-glass"
               style={{ color: loading ? "#f97316" : undefined, transition: "color 0.2s" }} />
            <input type="text" placeholder="Buscar factura, concepto…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }} />
            {search && (
              <button onClick={() => { setSearch(""); setPage(1); }}
                style={{ background: "none", border: "none", color: "#6b7280",
                  cursor: "pointer", fontSize: "0.8rem" }}>✕</button>
            )}
          </div>
        </div>

        {/* Tabla */}
        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th>N° Doc / Referencia</th>
                <th>Gasto / Período</th>
                <th>Proveedor</th>
                <th>Fecha</th>
                <th><div style={{ textAlign:"right" }}>Monto</div></th>
                <th className="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ textAlign: "center", padding: 32, color: "#9ca3af" }}>
                  <i className="fa-solid fa-circle-notch fa-spin"
                     style={{ marginRight: 8, color: "#f97316" }} />
                  Cargando facturas…
                </td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: "center", padding: 32, color: "#6b7280" }}>
                  <i className="fa-solid fa-inbox"
                     style={{ marginRight: 8, fontSize: "1.2rem" }} />
                  No hay facturas CxP.{" "}
                  <Link to="/finanzas/cxp/facturas/agregar" style={{ color: "#f97316" }}>
                    Agregar la primera
                  </Link>
                </td></tr>
              ) : items.map(fac => (
                <FacturaCxPRow key={fac.id} fac={fac}
                  onVer={() => setDetalle(fac)}
                  onEdit={() => setEditando(fac)}
                  onDelete={() => setDeleting(fac)} />
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer paginación — igual que ListInvoices */}
        <div className={invoiceStyles["table-footer"]}>
          <p>
            {loading ? (
              <span style={{ color: "#f97316", fontSize: "0.83rem" }}>
                <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 5 }} />
                Cargando…
              </span>
            ) : (
              <>
                <span>{count}</span> factura(s) · Página{" "}
                <span>{page}</span> de <span>{Math.max(1, totalPages)}</span>
              </>
            )}
          </p>
          <div className={`${invoiceStyles["pagination-buttons"]} d-flex gap-2`}>
            <button disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}
              className={`btn btn-sm ${invoiceStyles["btn-prev"]} d-flex gap-2 align-items-center`}>
              Anterior
            </button>
            <button disabled={page >= totalPages || loading} onClick={() => setPage(p => p + 1)}
              className={`btn btn-sm ${invoiceStyles["btn-next"]} d-flex gap-2 align-items-center`}>
              Siguiente
            </button>
          </div>
        </div>
      </div>

      {editando && (
        <EditFacturaModal fac={editando} onClose={() => setEditando(null)}
          onSaved={(msg, updated) => {
            showMsg(true, msg);
            // Update row in list with new data
            setItems(prev => prev.map(f => f.id === updated.id ? updated : f));
            setEditando(null);
          }} />
      )}

      {/* Modal detalle */}
      {detalle && (
        <div style={{ position:"fixed", inset:0, zIndex:1000,
          background:"rgba(0,0,0,0.75)", backdropFilter:"blur(3px)",
          display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={() => setDetalle(null)}>
          <div style={{ background:"#1f2937", border:"1px solid #364250",
            borderRadius:8, width:"min(560px,96vw)", maxHeight:"90vh", overflow:"auto" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding:"16px 20px", borderBottom:"1px solid #364250",
              display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <h2 style={{ margin:0, fontSize:16, fontWeight:600, color:"#e5ecf5", fontFamily:"monospace" }}>
                  {detalle.numero_doc_proveedor || detalle.numero_factura}
                </h2>
                <p style={{ margin:"3px 0 0", color:"#8d98a7", fontSize:13 }}>
                  {detalle.numero_factura} · Emitida: {fmtDate(detalle.fecha_emision)} · Vence: {fmtDate(detalle.fecha_vencimiento) || "—"}
                </p>
              </div>
              <button onClick={() => setDetalle(null)}
                style={{ background:"none", border:"none", color:"#8d98a7", cursor:"pointer", fontSize:"1.1rem" }}>
                <i className="fa-solid fa-xmark"/>
              </button>
            </div>
            <div style={{ padding:"16px 20px" }}>
              {[
                ["N° Doc Proveedor",  detalle.numero_doc_proveedor || "—"],
                ["N° Factura (int.)", detalle.numero_factura],
                ["Concepto",          detalle.concepto],
                ["Gasto (DEBE)",      detalle.cuenta_gasto?.nombre ?? "—"],
                ["Proveedor (HABER)", detalle.cuenta_proveedor?.nombre ?? "—"],
                ["Monto",             `₡${Number(detalle.monto || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
                ["Fecha emisión",     fmtDate(detalle.fecha_emision)],
                ["Fecha vencimiento", fmtDate(detalle.fecha_vencimiento) || "—"],
              ].map(([label, val], i) => (
                <div key={label} style={{ display:"flex", justifyContent:"space-between",
                  padding:"9px 4px", borderBottom:"1px solid #364250",
                  background: i % 2 === 0 ? "rgba(55,65,81,0.05)" : "transparent" }}>
                  <span style={{ color:"#8d98a7", fontSize:13 }}>{label}</span>
                  <span style={{ color:"#e5ecf5", fontSize:13, fontWeight:600, textAlign:"right",
                    maxWidth:"60%", wordBreak:"break-word" }}>{val}</span>
                </div>
              ))}
              <div style={{ marginTop:14, padding:"10px 12px",
                background:"rgba(55,65,81,0.1)", borderRadius:6, border:"1px solid #364250",
                fontFamily:"monospace", fontSize:12, color:"#8d98a7", lineHeight:1.9 }}>
                <span style={{ color:"#f87171" }}>DEBE</span>{"  "}{detalle.cuenta_gasto?.nombre ?? "—"}<br/>
                <span style={{ color:"#60a5fa" }}>HABER</span>{"  "}{detalle.cuenta_proveedor?.nombre ?? "—"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ConfirmModal eliminar */}
      <ConfirmModal
        open={!!deleting}
        name={deleting?.numero_factura}
        message={`Se eliminará ${deleting?.numero_factura} y su asiento contable. Si tiene recibos asociados, también serán eliminados y los saldos bancarios revertidos. Esta acción no se puede deshacer.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
        loading={delLoad}
      />
    </>
  );
}
